/* eslint-disable @typescript-eslint/naming-convention */

import { FocusableOption, FocusOptions, FocusOrigin } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { hasModifierKey } from '@angular/cdk/keycodes';
import {
    AfterViewChecked,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    InjectionToken,
    Input,
    OnDestroy,
    Output,
    input,
    model,
    inject,
    signal
} from '@angular/core';
import { Subject } from 'rxjs';

/**
 * Option IDs need to be unique across components, so this counter exists outside of
 * the component definition.
 */
let _uniqueIdCounter = 0;

/** Event object emitted by ApOption when selected or deselected. */
export class ApAutocompleteOptionSelectionChange {
    constructor(
        /** Reference to the option that emitted the event. */
        public source: ApAutocompleteOptionComponent,
        /** Whether the change in the option's value was a result of a user action. */
        public isUserInput = false
    ) {}
}

/**
 * Describes a parent component that manages a list of options.
 * Contains properties that the options can inherit.
 *
 * @docs-private
 */
export interface ApOptionParentComponent {
    disableRipple?: boolean;
    multiple?: boolean;
}

/**
 * Injection token used to provide the parent component to options.
 */
export const AP_OPTION_PARENT_COMPONENT = new InjectionToken<ApOptionParentComponent>('AP_OPTION_PARENT_COMPONENT');

/**
 * Single option inside of a `<ap-autocomplete>` element.
 */
@Component({
    selector: 'ap-autocomplete-option',
    host: {
        role: 'option',
        '[attr.tabindex]': 'getTabIndex()',
        '[class.ap-selected]': 'selected()',
        '[class.ap-active]': 'active()',
        '[id]': 'id()',
        '[attr.aria-selected]': 'getAriaSelected()',
        '[attr.aria-disabled]': 'disabled.toString()',
        '[class.ap-option-disabled]': 'disabled',
        '(click)': 'selectViaInteraction()',
        '(keydown)': 'handleKeydown($event)',
        class: 'ap-autocomplete-option'
    },
    templateUrl: './option.component.html',
    styleUrls: ['./option.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ApAutocompleteOptionComponent implements FocusableOption, AfterViewChecked, OnDestroy {
    private readonly _element = inject<ElementRef<HTMLElement>>(ElementRef);

    /** The unique ID of the option. */
    public readonly id = model<string>(undefined);

    /** Event emitted when the option is selected or deselected. */
    @Output() public readonly selectionChange = new EventEmitter<ApAutocompleteOptionSelectionChange>();

    /** Whether the option is disabled. */
    @Input()
    get disabled() {
        return this._disabled();
    }

    set disabled(value: any) {
        this._disabled.set(coerceBooleanProperty(value));
    }

    /** Whether ripples for the option are disabled. */
    get disableRipple() {
        return true;
    }

    /**
     * The displayed value of the option. It is necessary to show the selected option in the
     * select's trigger.
     */
    get viewValue(): string {
        return (this.getHostElement().textContent || '').trim();
    }

    /** Required - The form value of the option. */
    readonly value = input.required<any>();

    /** Required - The term that is being searched on */
    public readonly searchTerm = input.required<string>();

    public readonly labels = input<string[]>(undefined);

    /** Emits when the state of the option changes and any parents have to be notified. */
    readonly _stateChanges = new Subject<void>();
    public selected = signal(false);
    public active = signal(false);
    private _disabled = signal(false);
    private _mostRecentViewValue = '';

    constructor() {
        if (!this.id()) {
            _uniqueIdCounter++;
            this.id.set(`ap-option-${_uniqueIdCounter}`);
        }
    }

    /** Selects the option. */
    public select(): void {
        if (!this.selected()) {
            this.selected.set(true);
            this.emitSelectionChangeEvent();
        }
    }

    /** Deselects the option. */
    public deselect(): void {
        if (this.selected()) {
            this.selected.set(false);
            this.emitSelectionChangeEvent();
        }
    }

    /** Sets focus onto this option. */
    public focus(_origin?: FocusOrigin, options?: FocusOptions): void {
        // Note that we aren't using `_origin`, but we need to keep it because some internal consumers
        // use `ApOption` in a `FocusKeyManager` and we need it to match `FocusableOption`.
        const element = this.getHostElement();

        if (typeof element.focus === 'function') {
            element.focus(options);
        }
    }

    /**
     * This method sets display styles on the option to make it appear
     * active. This is used by the ActiveDescendantKeyManager so key
     * events will display the proper options as active on arrow key events.
     */
    public setActiveStyles(): void {
        if (!this.active()) {
            this.active.set(true);
        }
    }

    /**
     * This method removes display styles on the option that made it appear
     * active. This is used by the ActiveDescendantKeyManager so key
     * events will display the proper options as active on arrow key events.
     */
    public setInactiveStyles(): void {
        if (this.active()) {
            this.active.set(false);
        }
    }

    /**
     * `Selects the option while indicating the selection came from the user. Used to
     * determine if the select's view -> model callback should be invoked.`
     */
    public selectViaInteraction(): void {
        if (!this.disabled) {
            this.selected.set(true);
            this.emitSelectionChangeEvent(true);
        }
    }

    ngAfterViewChecked(): void {
        // Since parent components could be using the option's label to display the selected values
        // (e.g. `ap-select`) and they don't have a way of knowing if the option's label has changed
        // we have to check for changes in the DOM ourselves and dispatch an event. These checks are
        // relatively cheap, however we still limit them only to selected options in order to avoid
        // hitting the DOM too often.
        if (this.selected) {
            const { viewValue } = this;
            if (viewValue !== this._mostRecentViewValue) {
                this._mostRecentViewValue = viewValue;
                this._stateChanges.next();
            }
        }
    }

    ngOnDestroy(): void {
        this._stateChanges.complete();
    }

    /**
     * The primary label
     */
    protected get label(): string {
        return this.labels()[0] || '';
    }

    /**
     * List of secondary labels
     */
    protected get descriptions(): string[] {
        return this.labels().slice(1);
    }

    /** Write HTML string with bold search string */
    protected matchBold(value: string, searchTerm: string) {
        const regex = new RegExp(`(${searchTerm})`, 'gi');
        const replacedValue = value?.replace(regex, `<span class="match">$1</span>`);
        return searchTerm ? `<span class="result">${replacedValue}</span>` : value;
    }

    /** Ensures the option is selected when activated from the keyboard. */
    protected handleKeydown(event: KeyboardEvent): void {
        if ((event.code === 'Enter' || event.code === 'Space') && !hasModifierKey(event)) {
            this.selectViaInteraction();

            // Prevent the page from scrolling down and form submits.
            event.preventDefault();
        }
    }

    /**
     * Gets the `aria-selected` value for the option. We explicitly omit the `aria-selected`
     * attribute from single-selection, unselected options. Including the `aria-selected="false"`
     * attributes adds a significant amount of noise to screen-reader users without providing useful
     * information.
     */
    protected getAriaSelected(): boolean | null {
        return this.selected() || null;
    }

    /** Returns the correct tabindex for the option depending on disabled state. */
    protected getTabIndex(): string {
        return this.disabled ? '-1' : '0';
    }

    /** Emits the selection change event. */
    private emitSelectionChangeEvent(isUserInput = false): void {
        this.selectionChange.emit(new ApAutocompleteOptionSelectionChange(this, isUserInput));
    }

    /** Gets the host DOM element. */
    public getHostElement(): HTMLElement {
        return this._element.nativeElement;
    }
}

/**
 * Determines the position to which to scroll a panel in order for an option to be into view.
 *
 * @param optionIndex Index of the option to be scrolled into the view.
 * @param optionHeight Height of the options.
 * @param currentScrollPosition Current scroll position of the panel.
 * @param panelHeight Height of the panel.
 * @docs-private
 */

export const _getOptionScrollPosition = (
    optionIndex: number,
    optionHeight: number,
    currentScrollPosition: number,
    panelHeight: number
): number => {
    const optionOffset = optionIndex * optionHeight;

    if (optionOffset < currentScrollPosition) {
        return optionOffset;
    }

    if (optionOffset + optionHeight > currentScrollPosition + panelHeight) {
        return Math.max(0, optionOffset - panelHeight + optionHeight);
    }

    return currentScrollPosition;
};
